import { ChangeEvent, useMemo } from "react";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { Branch } from "../../types/user.type";
import { PurchaseOrderBranchRef } from "../../types/purchase-order.type";

/**
 * ค่าว่างของ dropdown — ยังไม่เลือกสาขา · ไม่บล็อกปุ่มยืนยันแล้ว แถวที่ยังว่างตอนบันทึก
 * กลายเป็น PO สถานะ ยังไม่ระบุสาขา ที่ HO ไประบุต่อที่ รายการ PO
 */
export const NO_BRANCH_CHOSEN = "";

/** ข้อความของค่าว่างในแถว — บอกผลของการปล่อยว่าง ไม่ใช่สั่งให้เลือก */
const DEFAULT_EMPTY_LABEL = "ยังไม่ระบุสาขา";

export const branchOptionLabel = (
  branch: PurchaseOrderBranchRef | Branch,
): string => `${branch.branch_code} — ${branch.branch_name}`;

interface BranchChoiceFieldProps {
  /** สาขาที่มีร้านถือรหัสนี้อยู่ — เสนอไว้บนสุดตามที่ handbook กำหนด */
  candidates: PurchaseOrderBranchRef[];
  /** สาขาทั้งหมด ใช้เมื่อไม่มีร้านไหนถือรหัสนี้เลย (candidates ว่าง) */
  branches: Branch[];
  value: string;
  onChange: (companyId: string) => void;
  /** ข้อความของค่าว่าง · แถบเลือกหลายแถวใช้ "เลือกสาขา" */
  emptyLabel?: string;
}

/**
 * เลือกสาขาให้แถวที่ระบบระบุสาขาไม่ได้
 *
 * ไฟล์ของเชนไม่มีคอลัมน์สาขา ระบบจึงรู้สาขาจากร้านที่จับคู่ได้เท่านั้น เมื่อรหัสร้าน
 * ไม่ตรงร้านไหนเลย หรือตรงมากกว่าหนึ่งร้าน คนต้องเป็นคนชี้สาขา — และถ้าสาขาที่เลือก
 * มีร้านที่ถือรหัสนี้อยู่ร้านเดียว backend จะผูกร้านนั้นให้เลย โดยนับเป็นการยืนยันของ HO
 */
const BranchChoiceField = ({
  candidates,
  branches,
  value,
  onChange,
  emptyLabel = DEFAULT_EMPTY_LABEL,
}: BranchChoiceFieldProps) => {
  const otherBranches = useMemo(() => {
    const candidateIds = new Set(
      candidates.map((candidate) => candidate.company_id),
    );
    return branches.filter((branch) => !candidateIds.has(branch.company_id));
  }, [candidates, branches]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  return (
    <TextField
      select
      fullWidth
      size="small"
      value={value}
      onChange={handleChange}
      slotProps={{ select: { displayEmpty: true } }}
      sx={{
        "& .MuiSelect-select": {
          color: value === NO_BRANCH_CHOSEN ? "warning.dark" : undefined,
        },
      }}
    >
      <MenuItem value={NO_BRANCH_CHOSEN}>{emptyLabel}</MenuItem>

      {candidates.length > 0 && (
        <ListSubheader>สาขาที่มีร้านรหัสนี้</ListSubheader>
      )}
      {candidates.map((candidate) => (
        <MenuItem key={candidate.company_id} value={candidate.company_id}>
          {branchOptionLabel(candidate)}
        </MenuItem>
      ))}

      {otherBranches.length > 0 && <ListSubheader>สาขาอื่น</ListSubheader>}
      {otherBranches.map((branch) => (
        <MenuItem key={branch.company_id} value={branch.company_id}>
          {branchOptionLabel(branch)}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default BranchChoiceField;
